import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart3, Leaf, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white rounded-2xl border border-[#E5D0C9] p-4 text-center">
    <p className="font-heading text-2xl font-bold" style={{ color: accent }}>{value}</p>
    <p className="text-[11px] text-[#8C7A76] uppercase tracking-wider">{label}</p>
  </div>
);

export default function AdminStats() {
  const [key, setKey] = useState(localStorage.getItem('innerglow_admin_key') || '');
  const [input, setInput] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/funnel-stats`, { params: { key: k, days: 14 } });
      setData(res.data);
      localStorage.setItem('innerglow_admin_key', k);
      setKey(k);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Invalid admin key' : 'Could not load stats');
      if (e.response?.status === 403) { localStorage.removeItem('innerglow_admin_key'); setKey(''); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  if (!key || (!data && !loading && error === 'Invalid admin key')) {
    return (
      <div className="min-h-screen bg-[#FFF2EC] flex items-center justify-center px-6" data-testid="admin-stats-login">
        <div className="bg-white rounded-2xl border border-[#E5D0C9] p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-6 h-6 text-[#FF6B4A]" />
            <h1 className="font-heading text-xl font-bold text-[#1A1514]">Funnel Stats</h1>
          </div>
          <Input type="password" placeholder="Admin key" value={input} onChange={(e) => setInput(e.target.value)}
            className="mb-3 h-11 rounded-xl" data-testid="admin-key-input" />
          {error && <p className="text-xs text-[#FF3B30] mb-2" data-testid="admin-key-error">{error}</p>}
          <Button onClick={() => load(input)} disabled={!input || loading}
            className="w-full bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full font-semibold"
            data-testid="admin-key-submit">View stats</Button>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};
  return (
    <div className="min-h-screen bg-[#FFF2EC] px-6 py-10" data-testid="admin-stats-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#FF6B4A]" />
            <h1 className="font-heading text-2xl font-bold text-[#1A1514]">Funnel — last {data?.days || 14} days</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(key)} className="rounded-full border-[#E5D0C9]" data-testid="admin-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Visits" value={t.visits ?? '—'} accent="#1A1514" />
          <StatCard label="Scan CTA clicks" value={t.cta_clicks ?? '—'} accent="#A388EE" />
          <StatCard label="Checkouts started" value={t.checkouts_started ?? '—'} accent="#FF6B4A" />
          <StatCard label="Paid" value={t.paid ?? '—'} accent="#159B4D" />
        </div>

        <div className="bg-white rounded-2xl border border-[#E5D0C9] overflow-hidden">
          <Table data-testid="funnel-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-[#8C7A76]">Date</TableHead>
                <TableHead className="text-[#8C7A76] text-right">Visits</TableHead>
                <TableHead className="text-[#8C7A76] text-right">CTA clicks</TableHead>
                <TableHead className="text-[#8C7A76] text-right">Checkouts</TableHead>
                <TableHead className="text-[#8C7A76] text-right">Paid</TableHead>
                <TableHead className="text-[#8C7A76] text-right">Pay rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.daily || []).map((r) => (
                <TableRow key={r.date} data-testid="funnel-row">
                  <TableCell className="text-[#1A1514] font-medium">{r.date}</TableCell>
                  <TableCell className="text-right">{r.visits}</TableCell>
                  <TableCell className="text-right">{r.cta_clicks}</TableCell>
                  <TableCell className="text-right">{r.checkouts_started}</TableCell>
                  <TableCell className="text-right font-bold text-[#159B4D]">{r.paid}</TableCell>
                  <TableCell className="text-right">{r.pay_rate}%</TableCell>
                </TableRow>
              ))}
              {(!data?.daily || data.daily.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-[#8C7A76] py-8">No events yet — data appears as visitors arrive.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <p className="text-[11px] text-[#8C7A76] mt-4">Visits are tracked once per browser session. "Paid" counts confirmed Stripe payments for the $1.99 scan.</p>
      </div>
    </div>
  );
}
